/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import './Groups.less';
import { AddPlusIcon } from '../Common/Icons';
import GroupsScheduleCard from './GroupsScheduleCard';
import { GroupModalActions } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  formatEventTimes,
  getTimeZoneFormat,
} from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const GroupSchedule = ({
  onGroupModalActionClick,
  selectedDate,
  scheduleEvents = [],
  setScheduleModalData,
  userInfo,
  loading,
  groupName,
  isStaffAdmin,
  isStaff,
  setupBanner,
  loginUserTimeformat,
}) => {
  const trans = useSelector(translator);
  const userTimezone = userInfo?.timezone ? getTimeZoneFormat(userInfo.timezone) : 'UTC';
  const is24HourFormat = loginUserTimeformat?.is24hoursformat || false;

  const formatDateToDisplayEventDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const transformedSchedule = useMemo(() => {
    if (!scheduleEvents || !selectedDate) return [];

    const selectedDateStr = selectedDate
      ? dayjs(selectedDate).tz(userTimezone).format('YYYY-MM-DD')
      : '';

    return scheduleEvents
      .filter((event) => {
        let eventDateTime;

        // Handle recurring events with display_event_date
        if (event.recurring && event.display_event_date) {
          return event.display_event_date === selectedDateStr;
        }

        // Handle non-recurring events
        if (event.event_timestamp) {
          eventDateTime = dayjs.utc(event.event_timestamp).tz(userTimezone);
        } else if (event.event_startdate && event.event_starttime) {
          const datePart = dayjs.utc(event.event_startdate).format('YYYY-MM-DD');
          const utcDateTimeStr = `${datePart}T${event.event_starttime}`;
          eventDateTime = dayjs.utc(utcDateTimeStr).tz(userTimezone);
        } else {
          return false;
        }

        const eventDateStr = eventDateTime.format('YYYY-MM-DD');
        return eventDateStr === selectedDateStr;
      })
      .map((event, index) => {
        let eventDateTime;

        if (event.event_timestamp) {
          eventDateTime = dayjs.utc(event.event_timestamp).tz(userTimezone);
        } else if (event.event_startdate && event.event_starttime) {
          const datePart = dayjs.utc(event.event_startdate).format('YYYY-MM-DD');
          const utcDateTimeStr = `${datePart}T${event.event_starttime}`;
          eventDateTime = dayjs.utc(utcDateTimeStr).tz(userTimezone);
        } else {
          eventDateTime = dayjs().tz(userTimezone); // fallback
        }

        const now = dayjs().tz(userTimezone);
        const diffInMinutes = now.diff(eventDateTime, 'minute');

        let status = 'Scheduled';
        if (diffInMinutes > (event.duration_minutes || 60)) {
          status = 'Completed';
        } else if (diffInMinutes >= -5 && diffInMinutes <= (event.duration_minutes || 60)) {
          status = 'Ongoing';
        }

        let timeString = '';
        if (event.event_startdate && event.event_starttime && event.event_endtime) {
          timeString = formatEventTimes(
            event.event_startdate,
            event.event_starttime,
            event.event_startdate,
            event.event_endtime,
            userTimezone,
            is24HourFormat
          );
        } else {
          const startTime = eventDateTime.format(is24HourFormat ? 'HH:mm' : 'h:mm A');
          const endTime = eventDateTime
            .add(event.duration_minutes || 60, 'minute')
            .format(is24HourFormat ? 'HH:mm' : 'h:mm A');
          timeString = `${startTime} - ${endTime}`;
        }

        let days = ['Once'];
        if (event.recurring && event.days_of_week?.length > 0) {
          days = event.days_of_week.map(
            (day) => day.charAt(0).toUpperCase() + day.slice(1, 3).toLowerCase()
          );
        } else if (event.repeat) {
          if (event.repeat === 'daily') {
            days = ['Daily'];
          } else if (['weekly', 'custom'].includes(event.repeat) && event.days_of_week) {
            days = event.days_of_week.map(
              (day) => day.charAt(0).toUpperCase() + day.slice(1, 3).toLowerCase()
            );
          }
        }

        const currentDate = dayjs().tz(userTimezone).startOf('day');

        let dayPrefix = eventDateTime.format('dddd');
        if (eventDateTime.isSame(currentDate, 'day')) {
          dayPrefix = 'Today';
        } else if (eventDateTime.isSame(currentDate.add(1, 'day'), 'day')) {
          dayPrefix = 'Tomorrow';
        } else if (eventDateTime.isSame(currentDate.subtract(1, 'day'), 'day')) {
          dayPrefix = 'Yesterday';
        }

        const translatedDayPrefix = trans(dayPrefix.toUpperCase()) || dayPrefix;
        const translatedMonth =
          trans(eventDateTime.format('MMMM').toUpperCase()) || eventDateTime.format('MMMM');
        const dateString = `${translatedDayPrefix}, ${translatedMonth} ${eventDateTime.date()}, ${eventDateTime.year()}`;

        return {
          id: index + 1,
          event_type:
            event.event_type === 'schedule' || event.event_type === 'zoom' ? 'Call' : 'Reminder',
          title: event.event_name,
          time: timeString,
          date: dateString,
          description: event.event_description,
          status: status,
          members: event.invited_contacts?.length || 0,
          days: days,
          group: 'Group',
          type: 'Group',
          invited_contacts: event.invited_contacts || [], // Add this for real participant data
          // Add original event data for reference
          originalEvent: event,
        };
      });
  }, [scheduleEvents, selectedDate, userTimezone, is24HourFormat]);

  return (
    <div className="groups-schedule-page">
      <div className="groups-add-call-button-container">
        {isStaffAdmin || isStaff ? (
          <div
            className="groups-add-call-button"
            onClick={() => onGroupModalActionClick(GroupModalActions.createScheduleCall)}
          >
            <AddPlusIcon className="groups-add-plus-icon" />
            <span className="groups-add-call-label">{trans('ADDCALL')}</span>
          </div>
        ) : null}
      </div>
      <Spin
        spinning={loading}
        style={{ height: setupBanner ? 'calc(100vh - 439px)' : 'calc(100vh - 280px)' }}
      >
        {!loading && (
          <GroupsScheduleCard
            scheduleData={transformedSchedule}
            onGroupModalActionClick={onGroupModalActionClick}
            setScheduleModalData={setScheduleModalData}
            groupName={groupName}
          />
        )}
      </Spin>
    </div>
  );
};

export default GroupSchedule;
